import s from './styles.module.scss'
import { useEffect, useState } from 'react'

interface InputFieldDateProps {
    fieldName: string
    label?: string
    hint?: string
    saveValue: (obj: { field: string; value: string }) => { success: boolean; error: string }
    populateValue?: string
    enhanced?: boolean
    required?: boolean
    showErrors?: boolean
    className?: string
    /** Inclusive. Defaults to 90 years ago (DOB range). */
    minYear?: number
    /** Inclusive. Defaults to 18 years ago (DOB range). */
    maxYear?: number
    /** Lets browser autofill fill the three dropdowns from a saved birthday */
    birthday?: boolean
}

export const InputFieldDate = (props: InputFieldDateProps) => {
    const {
        saveValue,
        fieldName,
        label,
        hint,
        populateValue,
        enhanced = false,
        required = false,
        showErrors = false,
        className,
        minYear: minYearProp,
        maxYear: maxYearProp,
        birthday = false,
    } = props
    const [day, setDay] = useState<string>('')
    const [month, setMonth] = useState<string>('')
    const [year, setYear] = useState<string>('')
    const [error, setError] = useState('')

    // Generate arrays for days, months, and years
    const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'))
    const months = [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' },
    ]

    const currentYear = new Date().getFullYear()
    const minYear = minYearProp ?? currentYear - 90
    const maxYear = maxYearProp ?? currentYear - 18
    const yearSpan = Math.max(0, maxYear - minYear + 1)
    const years = Array.from({ length: yearSpan }, (_, i) => String(maxYear - i))

    useEffect(() => {
        if (populateValue) {
            // Handle both YYYY-MM-DD (HTML date format) and DD/MM/YYYY (legacy format)
            if (populateValue.includes('-')) {
                const parts = populateValue.split('-')
                if (parts.length === 3) {
                    setYear(parts[0])
                    setMonth(parts[1])
                    setDay(parts[2])
                }
            } else {
                const parts = populateValue.split('/')
                if (parts.length === 3) {
                    setDay(parts[0])
                    setMonth(parts[1])
                    setYear(parts[2])
                }
            }
        }
    }, [populateValue])

    useEffect(() => {
        if (showErrors && required && (!day || !month || !year)) {
            setError('This field is required')
        } else {
            setError('')
        }
    }, [showErrors, required, day, month, year])

    const updateDate = (newDay: string, newMonth: string, newYear: string) => {
        if (newDay && newMonth && newYear) {
            setError('')

            // Format as YYYY-MM-DD (standard HTML date input format)
            const formattedDate = `${newYear}-${newMonth}-${newDay}`
            const saveResult = saveValue({
                field: fieldName,
                value: formattedDate,
            })

            if (!saveResult.success) {
                setError(saveResult.error)
            }
        } else if (required) {
            // Only show "complete all fields" error if showErrors is true
            // This prevents the error from flashing when user is still filling in the fields
            if (showErrors && (newDay || newMonth || newYear)) {
                setError('Please complete all date fields')
            } else {
                setError('')
            }

            saveValue({
                field: fieldName,
                value: '',
            })
        }
    }

    const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newDay = e.target.value
        setDay(newDay)
        updateDate(newDay, month, year)
    }

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newMonth = e.target.value
        setMonth(newMonth)
        updateDate(day, newMonth, year)
    }

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newYear = e.target.value
        setYear(newYear)
        updateDate(day, month, newYear)
    }

    return (
        <div className={s.fieldWrapper}>
            {label && <label className={s.label}>{label}</label>}
            {hint && <p className={s.hint}>{hint}</p>}
            <div className={`${s.datePickerContainer} ${enhanced ? s.enhanced : ''}`}>
                <select
                    className={`${s.dateSelect} ${enhanced ? s.enhancedSelect : ''} ${error ? s.error : ''} ${className || ''}`}
                    value={day}
                    onChange={handleDayChange}
                    autoComplete={birthday ? 'bday-day' : undefined}
                    aria-label="Day"
                >
                    <option value="">dd</option>
                    {days.map((d) => (
                        <option key={d} value={d}>
                            {d}
                        </option>
                    ))}
                </select>

                <select
                    className={`${s.dateSelect} ${enhanced ? s.enhancedSelect : ''} ${error ? s.error : ''} ${className || ''}`}
                    value={month}
                    onChange={handleMonthChange}
                    autoComplete={birthday ? 'bday-month' : undefined}
                    aria-label="Month"
                >
                    <option value="">mm</option>
                    {months.map((m) => (
                        <option key={m.value} value={m.value}>
                            {m.label}
                        </option>
                    ))}
                </select>

                <select
                    className={`${s.dateSelect} ${enhanced ? s.enhancedSelect : ''} ${error ? s.error : ''} ${className || ''}`}
                    value={year}
                    onChange={handleYearChange}
                    autoComplete={birthday ? 'bday-year' : undefined}
                    aria-label="Year"
                >
                    <option value="">yyyy</option>
                    {years.map((y) => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </select>

                {error && <span className={s.notice}>{error}</span>}
            </div>
        </div>
    )
}
